import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, HelpCircle, Upload } from 'lucide-react';
import { FieldCommentInline } from '../workflow/fieldComments';
import { useFieldInvalid, INVALID_FIELD_ATTR } from './formValidation';
import { acceptOf, fitsKind, formatLabelOf, maxBytesOf, sizeLabelOf } from '../../utils/uploadKinds';
import {
  btnChooseFile, fieldInvalidRing, fieldInvalidLabel, fieldInvalidText,
  fileDropRow, fileDropRowActive, fileDropReject, fileChip, fileChipRemove,
} from '../workflow/constants';

// Shared form styling + primitives — the single source of truth for modal
// forms across the app; never copy-paste `inputCls` or write a local
// `FieldRow` variant. See docs/conventions.md §form-kit.
//
// Design tokens tuned for a roomier, less-crowded feel:
// inputs py-2.5, modal body p-6 / space-y-4, grids gap-4.
// Iron rule #1: evolve the design language system-wide and consistently — use
// these tokens everywhere rather than re-defining styles per page.

// inputBaseCls has no width: pair it with a fixed width (w-28…). Adding a
// w-* to inputCls does nothing — its w-full wins in the generated CSS.
export const inputBaseCls = 'px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
export const inputCls = `w-full ${inputBaseCls}`;
export const selectCls = inputCls;
// A field the user cannot change here (read-only or disabled). Pair it with a
// `hint` saying where the value comes from, or wrap it in LockTip.
export const lockedInputCls = `${inputCls} bg-slate-100 text-slate-500 cursor-not-allowed`;
export const labelCls = 'block text-sm font-medium text-slate-700 mb-1.5';
export const btnPrimary = 'px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50';
export const btnGhost = 'px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg';
export const modalOverlayCls = 'fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4';

// Responsive column grids for laying fields out side by side.
export const grid2 = 'grid grid-cols-1 sm:grid-cols-2 gap-4';
export const grid3 = 'grid grid-cols-1 sm:grid-cols-3 gap-4';

// Modal width scale keyed to content density.
const SIZE = {
  sm: 'max-w-md',   // confirms, single small field
  md: 'max-w-lg',   // a few fields
  lg: 'max-w-2xl',  // standard create/edit
  xl: 'max-w-3xl',  // rich, multi-column forms (engagements, registers…)
};

/**
 * One form field: label (+ optional required asterisk / right-aligned action)
 * with an optional hint under the control. Superset of every previous FieldRow.
 *
 * `field` opts this control into the workflow return-comment layer:
 * when the request was sent back and the reviewer commented on THIS entry, the
 * comment renders under the control so the requester reads it while fixing it
 * (a single comment block at the top of the modal would mean scrolling up to
 * read and back down to type).
 *
 * Outside a workflow detail page there is no provider and `FieldCommentInline`
 * renders nothing, so every other form in the app is unaffected. The import
 * points at `workflow/` on purpose: `Field` is the one control every Modify
 * form already goes through, and a second rendering path would guarantee that
 * some flow eventually shows its comments somewhere else.
 *
 * The same `field` key drives submit-time required marking (`formValidation`):
 * inside a FormValidationProvider whose gap list names this key, the field
 * marks itself — red label, red halo, "Required" under the control — and
 * carries the data attribute the page scrolls to. No provider ⇒ no change.
 *
 * `error` is the live rule-violation caption — a value that IS filled in but
 * breaks a rule the page can judge on the spot (an archival date earlier than
 * the sign-off date). The page computes it from current state and passes the
 * text; the field wears the same red look as a required gap and shows the
 * message under the control the moment the wrong value is picked, instead of
 * only in the submit banner. Null/empty ⇒ nothing.
 */
export const Field = ({ label, required, hint, action, field = null, error = null, children }) => {
  const invalid = useFieldInvalid(field);
  const marked = !!(invalid || error);
  return (
    <div className={marked ? fieldInvalidRing : undefined} {...(marked && field ? { [INVALID_FIELD_ATTR]: field } : {})}>
      {(label || action) && (
        <div className="flex items-center justify-between gap-2 mb-1.5">
          {label ? (
            <label className={`block text-sm font-medium ${marked ? fieldInvalidLabel : 'text-slate-700'}`}>
              {label}{required && ' *'}
            </label>
          ) : <span />}
          {action}
        </div>
      )}
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      {invalid && <p className={fieldInvalidText}>Required</p>}
      {!invalid && error && <p className={fieldInvalidText}>{error}</p>}
      <FieldCommentInline field={field} />
    </div>
  );
};

// The dark help bubble shared by InfoTip, LockTip and other hover popups
// (look only — each caller positions it).
export const tooltipPopupCls = 'pointer-events-none z-50 rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-normal normal-case tracking-normal leading-relaxed text-white shadow-lg';
const infoTipBubbleCls = `${tooltipPopupCls} absolute top-full mt-1.5 opacity-0 invisible transition-opacity group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible`;

/**
 * Hover/focus help icon: keeps long explanatory copy off the page until asked
 * for (inline paragraphs make forms feel crowded).
 * Use it for guidance that is nice to have, never for text the user must read
 * to fill the field in (that stays a `hint`).
 *   <InfoTip text="One QC may cover several companies of the same group." />
 * `align` picks which edge the bubble hangs from ('left' default | 'right'),
 * so a tip near the right margin does not spill outside the card.
 */
export const InfoTip = ({ text, align = 'left', className = '' }) => (
  <span className={`relative inline-flex group align-middle ${className}`}>
    <button
      type="button"
      aria-label={typeof text === 'string' ? text : 'More information'}
      onClick={(e) => e.preventDefault()}
      className="text-slate-400 hover:text-slate-600 focus:text-slate-600 focus:outline-none"
    >
      <HelpCircle className="w-3.5 h-3.5" />
    </button>
    <span
      role="tooltip"
      className={`${infoTipBubbleCls} w-64 ${align === 'right' ? 'right-0' : 'left-0'}`}
    >
      {text}
    </span>
  </span>
);

/**
 * Wraps a control the user cannot change (a disabled input, checkbox or
 * button) and shows why on hover or keyboard focus, with no icon. The children
 * get no pointer events, so the hover always lands on the wrapper (a disabled
 * control would swallow it). The bubble is portalled to <body> with fixed
 * positioning, so a table or scroll box with `overflow` never clips it; near
 * the bottom of the window it opens above.
 *   <LockTip text="System default — cannot be changed"><input disabled … /></LockTip>
 * `block` makes the wrapper full width (around a full-width input); `align`
 * picks the edge the bubble lines up with. With no `text` the children render
 * as they are, so a conditional lock needs no branch.
 */
const LOCK_TIP_WIDTH = 256;
export const LockTip = ({ text, block = false, align = 'left', children }) => {
  const [anchor, setAnchor] = useState(null);
  useEffect(() => {
    if (!anchor) return undefined;
    const close = () => setAnchor(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [anchor]);
  if (!text) return children;
  const open = (e) => setAnchor(e.currentTarget.getBoundingClientRect());
  const display = block ? 'block' : 'inline-flex';
  let style = null;
  if (anchor) {
    const vw = window.innerWidth;
    const below = anchor.bottom + 80 < window.innerHeight;
    style = {
      position: 'fixed',
      maxWidth: LOCK_TIP_WIDTH,
      ...(below ? { top: anchor.bottom + 6 } : { bottom: window.innerHeight - anchor.top + 6 }),
      ...(align === 'right'
        ? { right: Math.max(8, vw - anchor.right) }
        : { left: Math.max(8, Math.min(anchor.left, vw - LOCK_TIP_WIDTH - 8)) }),
    };
  }
  return (
    <span
      className={`${display} cursor-not-allowed focus:outline-none`}
      tabIndex={0}
      aria-label={text}
      onMouseEnter={open}
      onFocus={open}
      onMouseLeave={() => setAnchor(null)}
      onBlur={() => setAnchor(null)}
    >
      <span className={`pointer-events-none ${display}`}>{children}</span>
      {anchor && createPortal(
        <span role="tooltip" style={style} className={`${tooltipPopupCls} w-max`}>{text}</span>,
        document.body
      )}
    </span>
  );
};

/**
 * Number input with its unit printed inside the box (`%`, `days`, `months`),
 * so the label reads as a plain question and the unit is never lost when the
 * box is narrow. `className` sizes the wrapper (a 12rem cap by default — a
 * one- or two-digit number does not need a full-width box).
 *   <UnitInput value={v} onChange={setV} unit="days" max={3650} />
 */
export const UnitInput = ({
  value, onChange, unit, min = 0, max, step = 1, disabled = false, className = 'max-w-[12rem]', ...rest
}) => (
  <div className={`relative ${className}`}>
    <input
      type="number" min={min} max={max} step={step} disabled={disabled}
      value={value === undefined || value === null ? '' : value}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} ${unit.length <= 1 ? 'pr-9' : unit.length <= 4 ? 'pr-14' : 'pr-[4.75rem]'} disabled:bg-slate-100 disabled:text-slate-400`}
      {...rest}
    />
    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">{unit}</span>
  </div>
);

// Dragging selected text, a link or an image out of another page also fires
// dragenter. Only a file drag may light the row up or be swallowed from the
// browser — everything else keeps its normal behaviour.
const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');

/**
 * The one file-picking control (house rule, docs/workflow-ui.md §4.3): a bare
 * <input type=file> prints its button text in the OS language — on a Chinese
 * Windows an English page suddenly grows two Chinese buttons. So the input is
 * always hidden behind a label styled like our outline buttons, with the
 * chosen file name(s) printed next to it in our own words.
 *
 * Pass-through onChange receives the native event untouched. Pass `files`
 * (the File objects the page holds) so the printed names follow the page's
 * state — clearing it after a save clears the name too. Call sites that
 * render their own selected-file list (rows with links, immediate uploads)
 * set `showSelection={false}` so the name is not shown twice.
 *
 * Each printed name carries its own remove button, so a wrong pick is undone
 * rather than only overwritten: a single-file field can go back to “nothing
 * picked”, and one file out of several can be dropped without re-picking the
 * rest. Removing rebuilds the hidden input the same way a drop does, so the
 * page hears about it through its ordinary onChange.
 *
 * The row also takes files dropped on it. A drop carries a DataTransfer, not
 * an input event, so the accepted files are written back into the hidden
 * input (`input.files = dt.files`) and the page's own onChange is called with
 * that input as the target — every call site keeps reading `e.target.files`
 * and none of them had to change.
 *
 * `kind` names a row of utils/uploadKinds — the one place that says what may
 * be uploaded and how big, shared with the multer config on the other side.
 * Never write an `accept` list here: a page that spells its own list out
 * drifts from the route that has to accept the file, and the file is refused
 * on submit instead of in the field. The kind is also checked on a drop and
 * on a pick, because the browser applies `accept` only to its own dialog and
 * never checks the size at all.
 */
export const FilePicker = ({
  multiple = false,
  kind,
  disabled = false,
  onChange,
  buttonLabel,
  showSelection = true,
  files,
  className = '',
}) => {
  const [picked, setNames] = useState([]);
  const [over, setOver] = useState(false);
  const [warn, setWarn] = useState('');
  const inputRef = useRef(null);
  const depth = useRef(0);               // dragenter/leave fire per child element
  const names = files ? [].concat(files).filter(Boolean).map((f) => f.name) : picked;

  // Shared by the dialog and the drop: wrong format, too big, or more than
  // one file in a single-file field.
  const screen = (list) => {
    const all = Array.from(list || []);
    const ok = all.filter((f) => fitsKind(kind, f.name) && f.size <= maxBytesOf(kind));
    const wrongType = all.filter((f) => !fitsKind(kind, f.name));
    const tooBig = all.filter((f) => fitsKind(kind, f.name) && f.size > maxBytesOf(kind));
    const reasons = [];
    if (wrongType.length) reasons.push(`${wrongType.map((f) => f.name).join(', ')} — only ${formatLabelOf(kind)} can be uploaded here`);
    if (tooBig.length) reasons.push(`${tooBig.map((f) => f.name).join(', ')} — over the ${sizeLabelOf(kind)} limit`);
    return { ok, warn: reasons.length ? `Not added: ${reasons.join('. ')}.` : '' };
  };

  const takeFiles = (list) => {
    const dropped = Array.from(list || []);
    if (!dropped.length) return;
    const { ok, warn } = screen(dropped);
    if (!ok.length) {
      setWarn(warn);
      return;
    }
    if (!multiple && ok.length > 1) {
      setWarn('Only one file can be uploaded here — drop a single file.');
      return;
    }
    const input = inputRef.current;
    let dt;
    try {
      dt = new DataTransfer();
      ok.forEach((f) => dt.items.add(f));
      input.files = dt.files;
    } catch (e) {
      setWarn('This browser cannot take dropped files — use the button instead.');
      return;
    }
    setWarn(warn);
    setNames(ok.map((f) => f.name));
    if (onChange) onChange({ target: input });
  };

  const dropAt = (idx) => {
    const input = inputRef.current;
    const left = Array.from((input && input.files) || []).filter((_, i) => i !== idx);
    try {
      const dt = new DataTransfer();
      left.forEach((f) => dt.items.add(f));
      input.files = dt.files;
    } catch (e) {
      input.value = '';                    // no DataTransfer: the field empties completely
      left.length = 0;
    }
    // Back to empty: clear `value` too, or picking the SAME file again fires
    // no change event and the field looks stuck.
    if (!left.length) input.value = '';
    setWarn('');
    setNames(left.map((f) => f.name));
    if (onChange) onChange({ target: input });
  };

  const dragProps = disabled ? {} : {
    onDragEnter: (e) => {
      if (!hasFiles(e)) return;            // dragging selected text or a link is not an upload
      e.preventDefault();
      depth.current += 1;
      setOver(true);
    },
    onDragOver: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e) => { e.preventDefault(); depth.current -= 1; if (depth.current <= 0) { depth.current = 0; setOver(false); } },
    onDrop: (e) => {
      e.preventDefault();
      depth.current = 0;
      setOver(false);
      takeFiles(e.dataTransfer && e.dataTransfer.files);
    },
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${fileDropRow} ${over ? fileDropRowActive : ''} ${className}`}
      {...dragProps}
    >
      <label className={`${btnChooseFile} ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <Upload className="w-4 h-4" />
        {buttonLabel || (multiple ? 'Choose Files' : 'Choose File')}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple={multiple}
          accept={acceptOf(kind)}
          disabled={disabled}
          onChange={(e) => {
            const { ok, warn } = screen(e.target.files);
            setWarn(warn);
            if (ok.length !== (e.target.files || []).length) {
              takeFiles(ok);                   // rebuild the input without the rejects
              return;
            }
            setNames(ok.map((f) => f.name));
            if (onChange) onChange(e);
          }}
        />
      </label>
      {showSelection && (
        names.length > 0
          ? names.map((n, i) => (
            <span key={`${n}-${i}`} className={fileChip}>
              <span className="break-all min-w-0">{n}</span>
              <button
                type="button"
                onClick={() => dropAt(i)}
                disabled={disabled}
                title={`Remove ${n}`}
                aria-label={`Remove ${n}`}
                className={fileChipRemove}
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          ))
          : <span className="text-sm text-slate-500">{multiple ? 'No files chosen — or drop them here' : 'No file chosen — or drop it here'}</span>
      )}
      {warn && <span className={fileDropReject}>{warn}</span>}
    </div>
  );
};

/**
 * Standard modal shell: centered card with header (title + close), a roomy
 * scrollable body, and an optional footer button row.
 *   <FormModal title="…" size="lg" onClose={…} footer={<>…buttons…</>}>…fields…</FormModal>
 * If `footer` is omitted, render your own button row as the last child.
 */
export const FormModal = ({ title, onClose, size = 'lg', footer, children, bodyClassName = '' }) => (
  <div className={modalOverlayCls}>
    <div className={`bg-white rounded-lg shadow-xl w-full ${SIZE[size] || SIZE.lg} max-h-[90vh] overflow-y-auto`}>
      <div className="flex items-center justify-between p-6 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className={`p-6 space-y-4 ${bodyClassName}`}>{children}</div>
      {footer && (
        <div className="flex justify-end gap-2 px-6 pb-6">{footer}</div>
      )}
    </div>
  </div>
);
